import { useEffect, useState, useCallback } from 'react';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Link, Navigate } from 'react-router-dom';
import { ShieldAlert, ArrowLeft, RefreshCw, Loader2, Trash2, Check, Bot, User, BarChart3 } from 'lucide-react';

const STATUSES = ['new', 'under_review', 'action_taken', 'no_violation', 'closed'];

const SEV_STYLE = {
  high: 'bg-red-600 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-navy/10 text-navy/70',
};

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl p-4 card-soft" data-testid={`mod-stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <p className="text-[11px] uppercase tracking-widest text-navy/45 font-semibold">{label}</p>
      <p className="font-heading text-2xl mt-1">{value}</p>
      {sub ? <p className="text-[11px] text-navy/50 mt-0.5">{sub}</p> : null}
    </div>
  );
}

export default function AdminCommunity() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState('all');
  const [status, setStatus] = useState('new');
  const [busy, setBusy] = useState(null);

  const loadAnalytics = useCallback(async () => {
    try {
      const r = await http.get('/community/admin/analytics', { params: { community: community === 'all' ? undefined : community } });
      setAnalytics(r.data);
    } catch { /* non-blocking */ }
  }, [community]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get('/community/admin/reports', { params: { community: community === 'all' ? undefined : community, status } });
      setItems(r.data.items || []);
    } catch { toast.error('Could not load reports'); }
    finally { setLoading(false); }
  }, [community, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const setReportStatus = async (r, newStatus) => {
    setBusy(r.id);
    try { await http.post(`/community/admin/reports/${r.id}/status`, { status: newStatus }); setItems((prev) => prev.map((x) => x.id === r.id ? { ...x, status: newStatus } : x)); toast.success('Updated'); }
    catch { toast.error('Update failed'); } finally { setBusy(null); }
  };
  const removeTarget = async (r) => {
    if (!window.confirm(`Remove this ${r.target_type}?`)) return;
    setBusy(r.id);
    try {
      if (r.target_type === 'post') await http.post(`/community/admin/posts/${r.target_id}/moderate`, { action: 'remove' });
      else await http.post(`/community/admin/comments/${r.target_id}/remove`);
      await setReportStatus(r, 'action_taken');
      toast.success('Content removed');
    } catch { toast.error('Remove failed'); } finally { setBusy(null); }
  };

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-5 lg:p-10 space-y-6" data-testid="admin-community-page">
      <Link to="/app/admin" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy" data-testid="back-to-admin"><ArrowLeft size={14}/> Back to Admin</Link>
      <div className="flex items-center gap-3">
        <ShieldAlert className="text-gold" size={22}/>
        <div>
          <h1 className="font-heading text-3xl">Community Moderation</h1>
          <p className="text-sm text-navy/55">Review reported posts and comments across both communities.</p>
        </div>
      </div>

      {analytics && (
        <div className="space-y-3" data-testid="mod-analytics">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="Open reports" value={analytics.reports_open} sub={`${analytics.reports_ai} AI · ${analytics.reports_user} user`}/>
            <StatCard label="Posts" value={analytics.posts_total} sub={analytics.posts_removed ? `${analytics.posts_removed} removed` : 'none removed'}/>
            <StatCard label="Comments" value={analytics.comments_total}/>
            <StatCard label="AI flags" value={Object.values(analytics.ai_flags_by_severity || {}).reduce((a, b) => a + b, 0)} sub={`${analytics.ai_flags_by_severity?.high || 0} high · ${analytics.ai_flags_by_severity?.medium || 0} med`}/>
          </div>
          {Object.keys(analytics.ai_flags_by_category || {}).length > 0 && (
            <div className="bg-white rounded-2xl p-4 card-soft">
              <p className="text-[11px] uppercase tracking-widest text-navy/45 font-semibold flex items-center gap-1.5"><BarChart3 size={13}/> AI flags by category</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(analytics.ai_flags_by_category).map(([cat, n]) => (
                  <span key={cat} className="text-xs rounded-full px-3 py-1 bg-beige text-navy/75">{cat.replace('_', ' ')} · {n}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl p-4 card-soft flex flex-wrap items-center gap-2">
        {['all', 'brotherhood', 'supporter'].map((c) => (
          <button key={c} onClick={() => setCommunity(c)} data-testid={`mod-community-${c}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ring-1 ${community === c ? 'bg-navy text-white ring-navy' : 'bg-white text-navy/70 ring-navy/10'}`}>
            {c === 'all' ? 'All' : c === 'brotherhood' ? 'Brotherhood' : 'Supporter'}
          </button>
        ))}
        <div className="w-px h-5 bg-navy/10 mx-1"/>
        {['new', 'under_review', 'action_taken', 'all'].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`mod-status-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ring-1 ${status === s ? 'bg-gold/20 text-navy ring-gold/40' : 'bg-white text-navy/70 ring-navy/10'}`}>
            {s.replace('_', ' ')}
          </button>
        ))}
        <button onClick={() => { load(); loadAnalytics(); }} className="ml-auto px-4 py-2 rounded-lg bg-navy text-white text-sm flex items-center gap-2" data-testid="mod-refresh"><RefreshCw size={14}/> Refresh</button>
      </div>

      <div className="space-y-3">
        {loading && <div className="bg-white rounded-2xl p-8 text-center card-soft"><Loader2 className="inline animate-spin" size={18}/></div>}
        {!loading && items.length === 0 && <div className="bg-white rounded-2xl p-8 text-center text-navy/50 card-soft" data-testid="mod-empty">No reports here.</div>}
        {!loading && items.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl p-5 card-soft" data-testid={`mod-report-${r.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              {r.source === 'ai' ? (
                <span className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-semibold bg-navy text-gold inline-flex items-center gap-1" data-testid={`mod-source-ai-${r.id}`}><Bot size={11}/> AI flag</span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-semibold bg-navy/5 text-navy/60 inline-flex items-center gap-1"><User size={11}/> User</span>
              )}
              {r.severity && <span className={`text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-semibold ${SEV_STYLE[r.severity] || 'bg-navy/10 text-navy/70'}`}>{r.severity}</span>}
              <span className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-semibold bg-red-50 text-red-700 ring-1 ring-red-200">{r.reason}</span>
              <span className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 bg-beige text-navy/70">{r.community} · {r.target_type}</span>
              <span className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 bg-navy/5 text-navy/60">{r.status?.replace('_', ' ')}</span>
              <span className="ml-auto text-[10px] text-navy/40">{new Date(r.created_at).toLocaleString()}</span>
            </div>
            {r.detail && <p className="mt-2 text-xs text-navy/60 italic">“{r.detail}”</p>}
            <div className="mt-3 rounded-xl bg-[#F9F8F6] border border-navy/5 p-3">
              <p className="text-[11px] text-navy/45">Reported {r.target_type} {r.target?.removed ? '(already removed)' : ''} — author: {r.target?.author_name || 'unknown'} {r.target?.identity === 'anonymous' ? '(posted anonymously)' : ''}</p>
              <p className="text-sm text-navy/85 mt-1 whitespace-pre-wrap">{r.target?.text || '—'}</p>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button onClick={() => removeTarget(r)} disabled={busy === r.id || r.target?.removed} className="text-[12px] px-3 py-1.5 rounded-full bg-red-600 text-white disabled:opacity-40 inline-flex items-center gap-1" data-testid={`mod-remove-${r.id}`}><Trash2 size={12}/> Remove content</button>
              <select value={r.status} onChange={(e) => setReportStatus(r, e.target.value)} disabled={busy === r.id} data-testid={`mod-set-status-${r.id}`}
                className="text-xs bg-white border border-navy/10 rounded-full px-3 py-1.5">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
              <button onClick={() => setReportStatus(r, 'no_violation')} disabled={busy === r.id} className="text-[12px] px-3 py-1.5 rounded-full bg-green/10 text-green inline-flex items-center gap-1" data-testid={`mod-dismiss-${r.id}`}><Check size={12}/> No violation</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
